<template>
  <div class="px-6 py-6 bg-zinc-950 min-h-screen text-zinc-400 font-light flex items-center justify-center">
    <div class="max-w-md w-full">
      <div v-if="isVerifying" class="bg-zinc-900/20 border border-zinc-900 rounded-2xl p-8 text-center space-y-6">
        <div class="w-16 h-16 bg-purple-950/40 border border-purple-900/40 rounded-full mx-auto flex items-center justify-center">
          <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-400"></div>
        </div>
        <div class="space-y-2">
          <h1 class="text-xl font-medium text-white">Vérification du paiement...</h1>
          <p class="text-sm text-zinc-500">Veuillez patienter</p>
        </div>
      </div>

      <div v-else-if="paymentSuccess" class="bg-zinc-900/20 border border-emerald-900/40 rounded-2xl p-8 text-center space-y-6">
        <div class="w-16 h-16 bg-emerald-950/40 border border-emerald-900/40 rounded-full mx-auto flex items-center justify-center">
          <i class="fas fa-check text-emerald-400 text-2xl"></i>
        </div>
        <div class="space-y-2">
          <h1 class="text-xl font-medium text-white">Paiement confirmé !</h1>
          <p class="text-sm text-zinc-500">Votre transaction a été enregistrée avec succès</p>
        </div>

        <!-- Bouton d'accompagnement pour les nouveaux abonnements -->
        <div v-if="isSubscriptionPayment && !redirectCancelled" class="space-y-3 pt-2">
          <div class="h-px bg-zinc-800"></div>
          <div class="space-y-2">
            <p class="text-sm text-zinc-400">Besoin d'aide pour démarrer ?</p>
            <button
              @click="bookAccompagnement"
              class="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 px-4 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2">
              <i class="fas fa-calendar-check"></i>
              Prendre rendez-vous pour un accompagnement
            </button>
          </div>
        </div>

        <div v-if="!redirectCancelled" class="text-xs text-zinc-600">
          Redirection en cours...
        </div>
      </div>

      <div v-else class="bg-zinc-900/20 border border-red-900/40 rounded-2xl p-8 text-center space-y-6">
        <div class="w-16 h-16 bg-red-950/40 border border-red-900/40 rounded-full mx-auto flex items-center justify-center">
          <i class="fas fa-times text-red-400 text-2xl"></i>
        </div>
        <div class="space-y-2">
          <h1 class="text-xl font-medium text-white">Erreur de paiement</h1>
          <p class="text-sm text-zinc-500">{{ errorMessage }}</p>
        </div>
        <button @click="returnToCashRegister" class="w-full bg-zinc-800 hover:bg-zinc-700 text-zinc-300 py-3 rounded-xl text-xs font-medium uppercase tracking-wider transition-colors">
          Retour à la caisse
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'
import api from '@/api'

const route = useRoute()
const router = useRouter()

const isVerifying = ref(true)
const paymentSuccess = ref(false)
const errorMessage = ref('')
const isSubscriptionPayment = ref(false)
const redirectCancelled = ref(false)
let redirectTimeout = null

onMounted(async () => {
  const sessionId = route.query.session_id
  const planType = route.query.plan

  if (!sessionId) {
    errorMessage.value = 'Session de paiement introuvable'
    isVerifying.value = false
    return
  }

  try {
    console.log('🔍 Vérification du paiement Stripe:', sessionId)

    let response

    // Si c'est un paiement d'abonnement ou de crédits SMS
    if (planType) {
      console.log('📦 Type de plan:', planType)
      response = await api.subscription.verifySubscription(sessionId)

      if (response.data.success) {
        paymentSuccess.value = true
        isSubscriptionPayment.value = true

        const messages = {
          solo: 'Abonnement Solo Studio activé !',
          equipe: 'Abonnement Équipe Lab activé !',
          salon: 'Abonnement Salon Plus Cloud activé !',
          lifetime: 'Accès Lifetime activé ! Bienvenue à vie 🎉'
        }

        toast.success(messages[planType] || 'Abonnement activé !', {
          position: 'top-right',
          autoClose: 3000
        })

        // Rediriger vers le dashboard après 5 secondes (plus de temps pour voir le bouton)
        redirectTimeout = setTimeout(() => {
          if (!redirectCancelled.value) {
            router.push('/')
          }
        }, 5000)
      } else {
        throw new Error('Abonnement non confirmé')
      }
    }
    // Sinon c'est un paiement de transaction classique
    else {
      response = await api.transactions.verifyStripePayment(sessionId)

      if (response.data.success) {
        paymentSuccess.value = true

        toast.success('Paiement confirmé !', {
          position: 'top-right',
          autoClose: 2000
        })

        // Rediriger vers le livre des transactions après 2 secondes
        setTimeout(() => {
          router.push('/finance')
        }, 2000)
      } else {
        throw new Error('Paiement non confirmé')
      }
    }

  } catch (error) {
    console.error('❌ Erreur lors de la vérification:', error)
    errorMessage.value = error.response?.data?.message || 'Une erreur est survenue lors de la vérification du paiement'
    isVerifying.value = false
  }
})

const bookAccompagnement = () => {
  // Annuler la redirection automatique
  redirectCancelled.value = true
  if (redirectTimeout) {
    clearTimeout(redirectTimeout)
  }

  toast.info('Redirection vers la prise de rendez-vous...', {
    position: 'top-right',
    autoClose: 1500
  })

  // Rediriger vers la création de rendez-vous après un court délai
  setTimeout(() => {
    router.push('/appointments/new?type=accompagnement')
  }, 1500)
}

const returnToCashRegister = () => {
  router.push('/caisse')
}
</script>
